import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { green, grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { QuickSendContext } from "contexts/QuickSend";
import { QuickSendContactsContext } from "contexts/QuickSendContacts";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { SubscriptionContext } from "contexts/Subscription";
import moment from "moment";
import { useContext, useMemo, useState } from "react";
import {
  BentoBrand,
  BentoContact,
  BentoContactSource,
  NewBentoContact,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import AddNewContactDialog from "features/Influencer/QuickSendIndividualDrawer/AddNewContact";
import { fetcherAuth } from "utils/api";
import { contactReplied } from "utils/influencer";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useRequests } from "utils/useRequests";

import ContactScore from "./ContactScore";
import { setContactKey } from "./helpers";
import styles from "./styles";

interface SelectContactProps {
  handleAddNewContact?: (
    newContact: NewBentoContact,
    handleClose: () => void,
  ) => void;
  brand: BentoBrand;
}

export default function SelectContact({
  handleAddNewContact,
  brand,
}: SelectContactProps) {
  const { updateContactNotifications } = useRequests();
  const { quickSendBrandId, previewLoading } = useContext(
    QuickSendDrawerContext,
  );
  const { setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    templateForQuickSend,
    setTemplateForQuickSend,
    handleRefetchPreviews,
    bentoContact,
  } = useContext(QuickSendContext);
  const { bentoContacts, fromMultipleCountries } = useContext(
    QuickSendContactsContext,
  );
  const { canMakeBrandRequest, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const { getLastContacted } = useContext(QuickSendContactsContext);
  const { openAddNewContact, setOpenAddNewContact } = useContext(
    QuickSendDrawerContext,
  );
  const { subscription, isVaPlan, isProPlan } = useContext(SubscriptionContext);
  const isSubscribedToPlan =
    subscription?.isSubscribed && (isVaPlan || isProPlan);

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const defaultNewContact = {
    bentoBrandId: brand?.id,
    name: "",
    email: "",
    title: "",
  };
  const [contact, setContact] = useState<NewBentoContact>(defaultNewContact);

  const handleCloseAddContactDialog = () => {
    setOpenAddNewContact(false);
  };

  const handleConfirm = (newContact: NewBentoContact) => {
    trackEvent("Add New Contact Button Clicked");
    if (handleAddNewContact) {
      handleAddNewContact(newContact, handleCloseAddContactDialog);
    }
  };

  const handleNotify = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/additional-contact-notifications`,
        "POST",
        {},
        {
          bentoBrandId: brand?.id,
        },
      );
      updateContactNotifications(res.contactNotification);
      setAlert(
        `We will notify you when a new contact is found for ${brand?.normalizedName}`,
        "success",
      );
    } catch (error) {
      setAlert(
        "Failed to submit request. Please reach out to hello@onbento.com for assistance",
        "error",
      );
    }
  };

  const handleChangeContact = (e: CustomEvent) => {
    const { value } = e.target;
    trackEvent("Contact Changed", {
      contact: value,
    });
    if (value === "new_contact") {
      setContact(defaultNewContact);
      setOpenAddNewContact(true);
    } else if (value === "notify_contact") {
      if (!canMakeBrandRequest()) {
        setUpgradeDialogSource("Hits Brand Request Limit");
        return;
      }
      handleNotify();
    } else {
      const copy = makeDeepCopy(templateForQuickSend);
      setTemplateForQuickSend(copy);
      handleRefetchPreviews(copy, value);
    }
  };

  const getFirstName = (name: string) => {
    if (name.includes("Team")) {
      return name;
    } else {
      return name.split(" ")[0];
    }
  };

  const renderContactSelectTitle = (
    contact: BentoContact | NewBentoContact | null,
  ) => {
    let titleAndName = "";
    if (contact?.name && contact?.title) {
      titleAndName = `${getFirstName(contact.name)}, ${contact.title}`;
    } else if (contact?.name) {
      titleAndName = getFirstName(contact.name);
    } else if (contact?.title) {
      titleAndName = contact.title;
    }

    const locationText =
      fromMultipleCountries && (contact as BentoContact)?.location
        ? `, ${(contact as BentoContact)?.location}`
        : "";
    return (
      <>
        {" "}
        {titleAndName}
        {locationText} ({contact?.email})
      </>
    );
  };

  const formatLastContacted = (contact: BentoContact | NewBentoContact) => {
    const lastContacted =
      "id" in contact && contact.id ? getLastContacted(contact.id) : null;
    if (lastContacted === undefined) {
      return (
        <CircularProgress
          sx={styles.loadingContactDate}
          size={10}
          disableShrink
        />
      );
    } else {
      return lastContacted ? (
        <Typography
          sx={styles.lastContacted}
          component={isMobileScreen ? "div" : "span"}
        >
          Contacted on {moment(lastContacted)?.format("YYYY-MMM-DD")}
        </Typography>
      ) : (
        <></>
      );
    }
  };

  function compare(
    a: BentoContact | NewBentoContact,
    b: BentoContact | NewBentoContact,
  ) {
    return (
      moment("id" in b && b.id ? getLastContacted(b?.id) : undefined).unix() -
      moment("id" in a && a.id ? getLastContacted(a?.id) : undefined).unix()
    );
  }

  const hasAccessToImportedContacts = currentOrg?.allowShareContacts;

  const sortedContacts = useMemo(
    () => bentoContacts?.sort(compare),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bentoContacts, getLastContacted],
  );

  const isLoading =
    previewLoading === quickSendBrandId || quickSendBrandId !== brand?.id;

  const renderContacts = () => {
    if (isLoading) {
      return (
        <Box sx={[styles.subjectWrapper, styles.border]}>
          <Typography component="span" sx={styles.annotation}>
            To:
          </Typography>
          <Skeleton animation="wave" width={100} />
        </Box>
      );
    } else {
      return (
        <Box sx={[styles.subjectWrapper, styles.border]}>
          <Typography component="span" sx={styles.annotation}>
            To:
          </Typography>

          {bentoContacts?.length > 0 ? (
            <>
              <FormControl>
                <Select
                  size="small"
                  value={setContactKey(bentoContact)}
                  onChange={handleChangeContact}
                  sx={styles.noSelectBorder}
                  renderValue={(id) => renderContactSelectTitle(bentoContact)}
                >
                  {sortedContacts?.map(
                    (
                      contact: BentoContact | NewBentoContact,
                      index: number,
                    ) => (
                      <MenuItem
                        key={index}
                        value={setContactKey(contact)}
                        style={{ whiteSpace: "normal" }}
                      >
                        {renderContactSelectTitle(contact)}{" "}
                        {formatLastContacted(contact)}{" "}
                        {"sourcedFromUserInbox" in contact &&
                          contact.sourcedFromUserInbox &&
                          isSubscribedToPlan &&
                          "💎"}
                        {"source" in contact &&
                          contact.source === BentoContactSource.gmail_import &&
                          hasAccessToImportedContacts && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: 12,
                                mx: 1,
                                color: "success.main",
                              }}
                              className="fa-solid fa-star"
                            />
                          )}
                        {isSubscribedToPlan &&
                          "bestResponseType" in contact &&
                          "lastSentToAt" in contact && (
                            <>
                              {contactReplied(contact) ? (
                                <Box
                                  component="i"
                                  sx={{ color: green[500], ml: 1 }}
                                  className="fa-regular fa-check-double"
                                />
                              ) : contact.lastSentToAt ? (
                                <Box
                                  component="i"
                                  sx={{ color: grey[400], ml: 1 }}
                                  className="fa-regular fa-check-double"
                                />
                              ) : (
                                <Box component="span"></Box>
                              )}
                            </>
                          )}
                      </MenuItem>
                    ),
                  )}
                  <MenuItem value="new_contact" sx={{ color: "green.main" }}>
                    <em>
                      Add your own contact{" "}
                      <Box component="i" className="fa-regular fa-edit" />
                    </em>
                  </MenuItem>
                  <MenuItem value="notify_contact" sx={{ color: "green.main" }}>
                    <em>
                      Request more contacts for this brand{" "}
                      <Box component="i" className="fa-regular fa-bell" />
                    </em>
                  </MenuItem>
                </Select>
              </FormControl>
              <ContactScore contacts={bentoContacts} />
            </>
          ) : (
            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: "none" }}
              onClick={() => setOpenAddNewContact(true)}
            >
              + Add Contact
            </Button>
          )}
        </Box>
      );
    }
  };

  return (
    <>
      {renderContacts()}

      {handleAddNewContact && (
        <AddNewContactDialog
          handleConfirm={handleConfirm}
          open={openAddNewContact}
          handleClose={handleCloseAddContactDialog}
          contact={contact}
          setContact={setContact}
        />
      )}
    </>
  );
}
